import { Injectable } from '@angular/core';
import { isNullOrUndefined } from 'util';
import * as L from 'leaflet';
import { NowcastRFConstants } from '../constant/nowcast-rainfall-map.constant';
import { delay } from 'rxjs/operators';
import { MapConstants } from 'src/app/utils/map.constant';
import { ProjConstants } from 'src/app/utils/proj.constant';
import { DataService } from './data.service';
import { HttpClient } from '@angular/common/http';
import { LandingPageService } from 'src/app/main/landing-page/landing-page.service';

@Injectable({
  providedIn: 'root'
})
export class MapService {

  stateBoundary: any;
  basinBoundary: any;

  constructor(
    private dataService: DataService,
    private http: HttpClient,
    private landingService: LandingPageService
  ) { }

  public createLayer(layer, gis) {
    if (layer.serviceType == 'HeatMap') {
      return this.createHeatMapLayer(layer, gis);
    } else if (layer.serviceType == 'WmsLayer') {
      return this.createWmsLayer(layer, gis);
    } else if (layer.serviceType == 'VectorLayer') {
      return this.createVectorLayer(layer, gis);
    } else if (layer.serviceType == 'WindLayer') {
      return this.createWindLayer(layer, gis);
    } else if (layer.serviceType == 'GeoJsonTileLayer') {
      return this.createGeoJsonTileLayer(layer, gis);
    } else {
      return new Promise((resolve, reject) => { resolve(true); });
    }
  }

  createGeoJsonTileLayer(layer, gis) {
    const options = {
      key: 'API_KEY',
      pane: 'state'
    };
    const applicationOptions = {};
    applicationOptions['time_stamp'] = layer.timeStamp;
    applicationOptions['component_type'] = layer.componentType;
    applicationOptions['gis'] = gis;
    applicationOptions['component_measure_unit'] = layer.componentMeasureUnit;
    applicationOptions['color'] = layer.color;
    const layerPromise = new Promise((resolve, reject) => {
      layer['layer'] = new L.GridLayer.GeoJSON(layer.url, options, applicationOptions);
      resolve(layer);
    });
    return layerPromise;
  }

  createWindLayer(layer, gis) {
    const layerPromise = new Promise((resolve, reject) => {
      const options = {
        displayValues: true,
        displayOptions: {
          velocityType: 'GBR Wind',
          displayPosition: 'bottomleft',
          displayEmptyString: 'No wind data',
        },
        paneName: 'Wind',
        minVelocity: 0,
        maxVelocity: 30,
        velocityScale: 0.009,
        particleAge: 50,
        lineWidth: 1,
        particleMultiplier: 0.0009,
        frameRate: 30,
        colorScale: ['rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)',
          'rgb(255,255,255)']
      };
      if (layer.url != undefined && layer.url != null) {
        this.dataService.getWindData(layer.url).then(
          data => {
            options['data'] = data;
            layer['layer'] = L.velocityLayer(options);
            resolve(layer);
          });
      }
    });
    return layerPromise;
  }

  createVectorLayer(layer, gis) {
    const options = {
      interactive: true,
      rendererFactory: {},
      opacity: 1,
      minZoom: 5,
      maxZoom: 18,
      pane: 'district',
      visible: true,
      transparent: true,
      vectorTileLayerStyles: {},
      getFeatureId: {}
    };
    options['rendererFactory'] = (L as any).canvas.tile;
    options['getFeatureId'] = (feature) => feature.properties.uuid;
    options['vectorTileLayerStyles'][layer.layerName] = (properties, zoom) => {
      const style = {
        opacity: 0,
      };
      return style;
    };
    const url = MapConstants.GEOSERVER_WMTS_URL + '&layer=' +
      MapConstants.GEOSERVER_WORKSPACE_NAME + ':' + layer.layerName;
    layer['layer'] = (L as any).vectorGrid.protobuf(url, options);
    return layer;
  }

  getLayerAttribution(source) {
    if (source === 'IMD') {
      return '<a href = \'https://mausam.imd.gov.in/\'> Data Source : IMD </a>';
    } else if (source === 'ECMWF') {
      return '<a href = \'https://www.ecmwf.int/\'> Data Source : ECMWF </a>';
    } else {
      return '<a href = \'https://mausam.imd.gov.in/\'> Data Source : IMD </a>';
    }
  }

  createWmsLayer(layer, gis) {
    const layerAttr = this.getLayerAttribution(gis.sourceSelected);
    const options = {
      layers: layer.layerName,
      opacity: 1,
      format: 'image/png',
      visible: true,
      transparent: true,
      pane: layer.zIndex ? layer.zIndex : 'district',
      fadeAnimation: false,
      attribution: layerAttr
    };
    if (layer.minZoom != null && layer.minZoom != undefined) {
      options['minZoom'] = layer.minZoom;
    }
    if (layer.stateName != null && layer.stateName != undefined) {
      options['viewParams'] = 'stateName:' + layer.stateName;
    }
    if (layer.env != null && layer.env != undefined) {
      options['env'] = layer.env;
    }
    if (layer.styles) {
      options['styles'] = layer.styles
    }
    if (layer.cql_filter != null && layer.cql_filter != undefined) {
      options['cql_filter'] = layer.cql_filter;
    }
    const url = MapConstants.GEOSERVER_WMS_URL;
    layer['layer'] = L.tileLayer.wms(url, options);
    return layer;
  }

  getImageBounds(imageBounds) {
    const anchors = [];
    anchors.push([imageBounds[1][0], imageBounds[0][1]]);
    anchors.push([imageBounds[1][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[1][1]]);
    anchors.push([imageBounds[0][0], imageBounds[0][1]]);
    return anchors;
  }

  createHeatMapLayer(layer, gis) {
    const layerPromise = new Promise((resolve, reject) => {
      const options = {
        opacity: 0.8,
        pane: 'heatMap',
        minZoom: 6,
        maxZoom: 15
      };
      this.getShapeBoundaryLayers(layer.view, layer.clipData).then(
        data => {
          if (data != null) {
            // options['clip'] = data;
          }
          console.log("NOWCAST LAYERR::",layer);
          
          if (layer.url != null && layer.url != undefined) {
            const anchors = layer['imageBounds'];
            layer['layer'] = L.imageTransform(layer['url'], anchors, options);
            layer['layer'].addTo(gis.map);
            gis.map.removeLayer(layer['layer']);
            resolve(layer);
          }
        });
    });
    return layerPromise;
  }

  createWeeklyHeatMapLayer(layer, gis) {
    const layerPromise2 = new Promise((resolve, reject) => {
      const options = {
        opacity: 0.8,
        pane: 'heatMap',
        minZoom: 6,
        maxZoom: 15
      };
      this.getShapeBoundaryLayers(layer.view, layer.clip).then(
        data => {
          if (data != null) {
            options['clip'] = data;
          }
          if (layer.url != null && layer.url != undefined && layer['tType'] != undefined && layer['tType'] == gis.meanMedian) {
            this.dataService.getHeatMap(layer.url).then(data => {
              if (data['url'] != null && data['url'] != undefined) {
                // layer['layer'] = L.imageTransform('assets/SampleCSV_imgs/wqValues_windspeed_432382632516.png',anchors,options);
                const anchors = this.getImageBounds(data['bounds']);
                layer['layer'] = L.imageTransform(data['url'], anchors, options);
                layer['layer'].addTo(gis.map);
                gis.map.removeLayer(layer['layer']);
              }
              resolve(layer);
            });
          }
        });
    });
    return layerPromise2;
  }

  getShapeBoundaryLayers(view, clip?) {
    const shapeBoundary = new Promise((resolve, reject) => {
      if (clip == false) {
        resolve(null);
      }
      if (view == 'Admin') {
        if (this.stateBoundary == null || this.stateBoundary == undefined) {
          const layerName = 'odisha' + ':' + ProjConstants.STATE_TO_LAYERTYPE_NAME['INDIA_BOUNDARY'];
          const boundaryURL = MapConstants.GEOJSON_BOUNDARY_LAYER_URL + '&typeName=' + layerName
          this.http.get(boundaryURL).subscribe(
            data => {
              this.stateBoundary = data['features'][0]['geometry'];
              resolve(this.stateBoundary);
            });
        } else {
          resolve(this.stateBoundary);
        }
      }
    });
    return shapeBoundary;
  }

  createPopUp(latlng, gis) {
    const myIcon = L.divIcon({ className: 'icon-dot', html: '<div class="pulsating-icon repeat"></div>' });
    const markerOptions = {
      icon: myIcon,
      pane: 'popUp'
    };
    if (gis.clickedLocationMarker != null && gis.clickedLocationMarker != undefined) {
      gis.clickedLocationMarker.unbindTooltip();
      gis.clickedLocationMarker.bindTooltip(gis.clickedLocationTooltip);
      gis.clickedLocationMarker = null;
    }
    if (gis.map.hasLayer(gis.marker)) {
      gis.map.removeLayer(gis.marker);
    }
    gis.marker = L.marker([latlng.lat, latlng.lng], markerOptions).addTo(gis.map);
  }

  async addTimeBasedLayers(gis, layerList) {
    gis.playButtonDisable = true;
    gis.labelStrings = [];
    const labels = [];
    let layerObjPromises = [];
    await delay(0.1);
    gis.showThrobber = true;
    for (const index in layerList) {
      const layer = layerList[index];
      if (layer['type'] == 'Time' && layer['view'] == gis.view && layer['time'] == gis.forecastLength && layer['setDefault'] == 'true' && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
        let layerObj: any;
        if (layer['layer'] != null && layer['layer'] != undefined) {
          layer['layer'].addTo(gis.map);
          gis.activeGroup = layer['groupName'];
          gis.activeLayers.push(layer);
          if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
            labels[layer['timeLinePos']] = {
              groupName: layer['groupName'],
              label: layer['timeLineName'],
              fullLabel: layer['leftSideName'],
            };
          }
          layerObj = new Promise((resolve) => { resolve(layer); });
        } else {
          layerObj = this.createLayer(layer, gis);
          layerObj.then(
            data => {
              if (data['layer'] != undefined && data['layer'] != null) {
                if (!isNullOrUndefined(layer['layerName'])) {
                  if (!isNullOrUndefined(gis.htmlLegend)) {
                    gis.map.removeControl(gis.htmlLegend);
                  }
                  const htmlLegend = this.createLegend();
                  gis.map.addControl(htmlLegend);
                  gis.htmlLegend = htmlLegend;

                  const legendInfoData = this.getLegendData(NowcastRFConstants.legendData, data['layer']);
                  htmlLegend.addLegend(legendInfoData);
                }
                data['layer'].addTo(gis.map);
                gis.activeGroup = data['groupName'];
                gis.activeLayers.push(data);
                if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
                  labels[layer['timeLinePos']] = {
                    groupName: layer['groupName'],
                    label: layer['timeLineName'],
                    fullLabel: layer['leftSideName'],
                  };
                }
              }
              layerList[index] = data;
            }
          );
        }
        layerObjPromises = layerObjPromises.concat(layerObj);
      }
    }
    Promise.all(layerObjPromises).then(
      data => {
        gis.labelStrings = JSON.parse(JSON.stringify(labels));
        gis.playButtonDisable = false;
        gis.showThrobber = false;
        this.landingService.startCarousel.next(true);
        this.landingService.throbberRunningStatus.throbberFR = false;
      });
    let count: any = 1;
    let start = 0;
    let end = 4;
    while (count != 0) {
      await this.fetchTimeLayers(gis, count, start, end, layerList, labels).then(
        data => {
          count = data;
        });
      start = end;
      end = end + 4;
    }
    if (gis.labelStrings[0] != undefined) {
      gis.initialCounter = parseInt(gis.labelStrings[0]['groupName'], 10);
    }
  }

  fetchTimeLayers(gis, count, start, end, layerList, labels) {
    return new Promise((resolve, reject) => {
      count = 0;
      let layerObjPromises = [];
      for (const index in layerList) {
        const layer = layerList[index];
        if (layer['type'] == 'Time' && layer['view'] == gis.view && layer['time'] == gis.forecastLength && layer['setDefault'] == 'false' && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
          if ((layer['layer'] == undefined || layer['layer'] == null) && layer['timeLinePos'] >= start && layer['timeLinePos'] < end) {
            const layerObj = this.createLayer(layer, gis);
            layerObj.then(
              data => {
                layerList[index] = data;
                if (data['layer'] != null && data['layer'] != undefined) {
                  if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
                    labels[layer['timeLinePos']] = {
                      groupName: layer['groupName'],
                      label: layer['timeLineName'],
                      fullLabel: layer['leftSideName'],
                    };
                  }
                }
                count++;
              }
            );
            layerObjPromises = layerObjPromises.concat(layerObj);
          } else if (layer['layer'] != null && layer['layer'] != undefined) {
            if (layer['timeLineName'] != null && layer['timeLineName'] != undefined) {
              labels[layer['timeLinePos']] = {
                groupName: layer['groupName'],
                label: layer['timeLineName'],
                fullLabel: layer['leftSideName'],
              };
            }
          }
        }
      }
      Promise.all(layerObjPromises).then(
        data => {
          gis.labelStrings = JSON.parse(JSON.stringify(labels));
          count = layerObjPromises.length;
          resolve(count);
        }
      );
    });
  }

  changeTimeLayers(gis, groupName) {
    if (groupName != gis.activeGroup) {
      const oldGroupName = gis.activeGroup;
      gis.activeGroup = groupName;
      this.removeGroupTimeLayers(gis, oldGroupName);
      this.addGroupTimeLayers(gis, groupName);
    }
  }

  addGroupTimeLayers(gis, groupName) {
    for (const index in gis.layerList) {
      const layer = gis.layerList[index];
      if (layer['type'] == 'Time' && layer['time'] == gis.forecastLength && layer['groupName'] == groupName && layer['view'] == gis.view && (layer['type2'] == undefined || layer['type2'] == gis.minMax)) {
        if (layer['layer'] != null && layer['layer'] != undefined) {
          layer['layer'].addTo(gis.map);
          gis.activeLayers.push(layer);
        }
      }
    }
  }

  removeGroupTimeLayers(gis, groupName) {
    for (const index in gis.activeLayers) {
      const layer = gis.activeLayers[index];
      if (layer['groupName'] == groupName && layer['type'] == 'Time') {
        gis.map.removeLayer(layer['layer']);
        gis.activeLayers[index] = 'deleted';
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  removeAllTimeLayers(gis) {
    for (const index in gis.activeLayers) {
      const layer = gis.activeLayers[index];
      if (layer['type'] == 'Time') {
        gis.map.removeLayer(layer['layer']);
        gis.activeLayers[index] = 'deleted';
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  removeAndAddInverseMask(gis, inverseInfo) {
    // console.log(inverseInfo);
    this.removeInverseMaskLayers(gis);
    this.addInverseMaskLayer(gis, inverseInfo);
  }

  removeInverseMaskLayers(gis) {
    for (const index in gis.activeLayers) {
      const layer = gis.activeLayers[index];
      if (layer['type'] == 'Inverse') {
        gis.map.removeLayer(layer['layer']);
        gis.activeLayers[index] = 'deleted';
      }
    }
    gis.activeLayers = gis.activeLayers.filter(item => item != 'deleted');
  }

  addInverseMaskLayer(gis, info) {
    if (isNullOrUndefined(info)) { return; }
    for (const index in gis.layerList) {
      const layer = gis.layerList[index];
      // console.log(layer);
      if (layer['type'] == 'Inverse' && layer['name'] == info.name && layer['view'] == gis.view) {
        // console.log('Entered Inverse');
        layer['cql_filter'] = info.cql_info;
        layer['hidelayer'] = false;
        const layerObj = this.createLayer(layer, gis);
        if (layerObj['layer'] != undefined && layerObj['layer'] != null) {
          layerObj['layer'].addTo(gis.map);
          gis.activeLayers = gis.activeLayers.concat(layerObj);
        }
        gis.layerList[index] = layerObj;
      }
    }
  }

  createLegend() {
    return new (L as any).Control.HtmlLegend({
      position: 'bottomright',
      collapseSimple: false,
      detectStretched: false,
      collapsedOnInit: false
    });
  }

  getLegendData(data, layerInstance) {
    const legend = {
      name: data.title,
      layer: layerInstance,
    };
    legend['elements'] = [];
    const isTrue = 1;
    if (isTrue == 1 && data.levels.length > 0) {
      let html = ``;
      for (let key = 0; key < data.levels.length; key++) {
        if (data.levels[key].image) {
          html += `<p style='height:30px;margin:0px;padding:4px;color:black;background-color: ${data.levels[key].color}'>
               ${data.levels[key].name} <img src='${data.levels[key].image}'></p>`;
        } else {
          html += `<p style='height:20px;margin:0px;padding:4px;color:black;background-color: ${data.levels[key].color}'>${data.levels[key].name}</p>`;
        }
      }
      legend['elements'].push({ html });
    } else if (data.levels) {
      let obj;
      if (!(data.levels instanceof Array)) {
        const periodWiseLegend = {};
        for (const period in data.levels) {
          if (!isNullOrUndefined(period)) {
            const tempLegend = {
              name: data.title,
              layer: layerInstance,
              elements: []
            };
            data.levels[period].forEach(level => {
              obj = {};
              obj['html'] = '';
              if (level['image'] != undefined && level['image'] != '') {
                obj['html'] = '<img style=\'width:13px;height:13px;\' src=\'' + level['image'] + '\'/>';
              }
              obj['label'] = level.name;
              obj['style'] = { 'background-color': level.color, width: '13px', height: '13px' };
              tempLegend.elements.push(obj);
            });
            periodWiseLegend[period] = tempLegend;
          }
        }
        return periodWiseLegend;
      } else {
        data.levels.forEach(level => {
          obj = {};
          obj['html'] = '';
          if (level['image'] != undefined && level['image'] != '') {
            obj['html'] = '<img style=\'width:13px;height:13px;\' src=\'' + level['image'] + '\'/>';
          }
          obj['label'] = level.name;
          obj['style'] = { 'background-color': level.color, width: '13px', height: '13px' };
          legend['elements'].push(obj);
        });
      }
    }
    return legend;
  }
}
